import {
  useDeleteQuery,
  useGetQuery,
  usePatchQuery,
  usePostQuery,
  useRouteParams
} from "@quortex/front-common"
import type { DeepPartial } from "react-hook-form"
import type { CdnListParams, CdnParams } from "@/queries/types"

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

const getCdnListUrl = ({ workspaceUuid, orgUuid, dryRun }: Required<CdnListParams>) =>
  `/workspaces/${workspaceUuid}/cdns/?org=${orgUuid}${dryRun ? "&dry_run=true" : ""}`

const getCdnUrl = ({ workspaceUuid, cdnUuid, orgUuid, dryRun }: Required<CdnParams>) =>
  `/workspaces/${workspaceUuid}/cdns/${cdnUuid}/?org=${orgUuid}${dryRun ? "&dry_run=true" : ""}`

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const useCdnList = (params?: CdnListParams) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getCdnListUrl(mergedParams)

  return useGetQuery<api.switchns.PaginatedCdnList>("switch", listUrl)
}

export const useCdn = (params?: CdnParams) => {
  const mergedParams = useRouteParams(params)
  const url = getCdnUrl(mergedParams)

  return useGetQuery<api.switchns.Cdn>("switch", url)
}

export const useAddCdn = (params?: CdnParams) => {
  const mergedParams = useRouteParams(params)
  const url = getCdnListUrl(mergedParams)

  type TRequest = DeepPartial<api.switchns.PatchedCdnRequest>
  type TResponse = api.switchns.Cdn

  return usePostQuery<TRequest, TResponse>("switch", url, [url])
}

export const useUpdateCdn = (params?: CdnParams) => {
  const mergedParams = useRouteParams(params)
  const url = getCdnUrl(mergedParams)
  type TRequest = DeepPartial<api.switchns.PatchedCdnRequest>
  type TResponse = api.switchns.Cdn

  return usePatchQuery<TRequest, TResponse>("switch", url, [url])
}

export const useDeleteCdn = (params?: CdnParams) => {
  const mergedParams = useRouteParams(params)
  const url = getCdnUrl(mergedParams)
  const listUrl = getCdnListUrl(mergedParams)

  return useDeleteQuery("switch", url, [listUrl])
}
